import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import Button from '../components/ui/button'
import { Input } from '../components/ui/input'
import { useLanguage } from '../context/LanguageContext'
import { useTenant } from '../context/TenantContext'
import { useClient } from '../context/ClientContext'
import apiService from '../services/api.service'

const OTP_LENGTH = 6

const ChangeEmail = () => {
  const { tenanturlname } = useParams()
  const { t, languageCode } = useLanguage()
  const { tenant } = useTenant()
  const { client } = useClient()

  const [newEmail, setNewEmail] = useState('')
  const [verifyEmail, setVerifyEmail] = useState('')
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(''))
  const inputRefs = useRef([])

  const [step, setStep] = useState('email')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const [isLocked, setIsLocked] = useState(false)
  const [lockMessage, setLockMessage] = useState('')

  const [otpTimeRemaining, setOtpTimeRemaining] = useState(null)
  const [otpExpired, setOtpExpired] = useState(false)

  const oldEmailForOtp =
    client?.email || sessionStorage.getItem('changeEmailOldEmail') || ''

  const otpValue = otpDigits.join('')
  const isOtpComplete = otpValue.length === OTP_LENGTH

  useEffect(() => {
    checkEmailChangeStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (client?.email) {
      sessionStorage.setItem('changeEmailOldEmail', client.email)
    }
  }, [client?.email])

  useEffect(() => {
    if (step !== 'otp') return

    const updateTimer = () => {
      const validityMinutes = parseInt(
        sessionStorage.getItem('otpValidityMinutes') || '60',
        10,
      )
      const sentAt = sessionStorage.getItem('otpSentAt')
      if (!sentAt) {
        setOtpTimeRemaining('00:00')
        return
      }

      const sentTime = new Date(sentAt).getTime()
      const elapsed = Math.floor((Date.now() - sentTime) / 1000)
      const totalSeconds = validityMinutes * 60
      const remaining = totalSeconds - elapsed

      if (remaining <= 0) {
        setOtpExpired(true)
        setOtpTimeRemaining('00:00')
      } else {
        const minutes = Math.floor(remaining / 60)
        const seconds = remaining % 60
        setOtpExpired(false)
        setOtpTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`)
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [step])

  // ── OTP digit-box handlers ──

  const focusInput = useCallback((index) => {
    if (index >= 0 && index < OTP_LENGTH) {
      inputRefs.current[index]?.focus()
    }
  }, [])

  const handleDigitChange = useCallback(
    (index, value) => {
      // Accept only a single digit
      const digit = value.replace(/\D/g, '').slice(-1)
      setOtpDigits((prev) => {
        const next = [...prev]
        next[index] = digit
        return next
      })
      setErrors((prev) => ({ ...prev, otp: undefined }))
      if (digit && index < OTP_LENGTH - 1) {
        focusInput(index + 1)
      }
    },
    [focusInput],
  )

  const handleDigitKeyDown = useCallback(
    (index, e) => {
      if (e.key === 'Backspace') {
        if (!otpDigits[index] && index > 0) {
          // Current box is empty — clear preceding box and move focus back
          setOtpDigits((prev) => {
            const next = [...prev]
            next[index - 1] = ''
            return next
          })
          focusInput(index - 1)
          e.preventDefault()
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        focusInput(index - 1)
        e.preventDefault()
      } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
        focusInput(index + 1)
        e.preventDefault()
      }
    },
    [otpDigits, focusInput],
  )

  const handleDigitPaste = useCallback((e) => {
    e.preventDefault()
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH)
    if (!pasted) return
    setOtpDigits((prev) => {
      const next = [...prev]
      for (let i = 0; i < OTP_LENGTH; i++) {
        next[i] = pasted[i] || ''
      }
      return next
    })
    // Focus the box after the last pasted digit, or the last box
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1)
    setTimeout(() => inputRefs.current[focusIdx]?.focus(), 0)
  }, [])

  const resetOtpDigits = useCallback(() => {
    setOtpDigits(Array(OTP_LENGTH).fill(''))
    setTimeout(() => focusInput(0), 0)
  }, [focusInput])

  const applyLimitReachedState = () => {
    const msg = t('changeEmail.errors.maxOtpSendsReached')
    setIsLocked(true)
    setLockMessage(msg)
    setErrors({ general: msg })
  }

  const checkEmailChangeStatus = async () => {
    if (!client?.clientId || !tenant?.tenantid) return

    try {
      const response = await apiService.post('/api/clients/emailchangestatus', {
        clientid: client.clientId,
        tenantid: tenant.tenantid,
      })

      if (response.isLocked) {
        applyLimitReachedState()
      }

      if (response.pendingChange) {
        setStep('otp')
        setNewEmail(response.pendingEmail || '')
        sessionStorage.setItem(
          'otpSentAt',
          response.ecotpsentdate || new Date().toISOString(),
        )
        sessionStorage.setItem(
          'otpValidityMinutes',
          String(response.otpValidityMinutes || 60),
        )
      }
    } catch (error) {
      if (
        error?.status === 429 ||
        error?.data?.code === 'EMAIL_CHANGE_LIMIT_REACHED'
      ) {
        applyLimitReachedState()
      }
    }
  }

  const validateEmailFormat = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    if (!newEmail) {
      setErrors({ newEmail: t('changeEmail.errors.newEmailRequired') })
      return
    }

    if (!verifyEmail) {
      setErrors({ verifyEmail: t('changeEmail.errors.verifyEmailRequired') })
      return
    }

    if (newEmail !== verifyEmail) {
      setErrors({ verifyEmail: t('changeEmail.errors.emailsDoNotMatch') })
      return
    }

    if (!validateEmailFormat(newEmail)) {
      setErrors({ newEmail: t('changeEmail.errors.invalidEmailFormat') })
      return
    }

    if (isLocked) {
      setErrors({ general: lockMessage })
      return
    }

    setIsLoading(true)

    try {
      const newDisplayEmail = newEmail.split('@')[0]
      const response = await apiService.post(
        '/api/clients/initiateemailchange',
        {
          clientid: client.clientId,
          tenantid: tenant.tenantid,
          newemail: newEmail,
          newdisplayemail: newDisplayEmail,
          languagecode: languageCode,
        },
      )

      if (response.success) {
        setStep('otp')
        resetOtpDigits()
        setOtpExpired(false)
        sessionStorage.setItem(
          'otpValidityMinutes',
          String(response.otpValidityMinutes || 60),
        )
        sessionStorage.setItem('otpSentAt', new Date().toISOString())
        return
      }

      if (response.code === 'OWMS0011') {
        const hours = response.cooldownHours || 48
        setErrors({
          general: t('changeEmail.errors.emailChangeCooldown').replace(
            '{hours}',
            hours,
          ),
        })
        return
      }

      if (
        response.code === 'EMAIL_CHANGE_LIMIT_REACHED' ||
        response.code === 'OWMS0008'
      ) {
        applyLimitReachedState()
        return
      }

      if (response.code === 'OWMS0005') {
        setErrors({ general: t('changeEmail.errors.existingEmail') })
        return
      }

      if (response.code === 'OWMS0012') {
        setErrors({
          general: t('changeEmail.errors.maxLifetimeChangesReached'),
        })
        return
      }

      setErrors({ general: t('changeEmail.errors.changeEmailFailed') })
    } catch (error) {
      const errorData = error?.data
      if (
        error?.status === 429 ||
        errorData?.code === 'EMAIL_CHANGE_LIMIT_REACHED'
      ) {
        applyLimitReachedState()
      } else if (errorData?.code === 'OWMS0011') {
        const hours = errorData.cooldownHours || 48
        setErrors({
          general: t('changeEmail.errors.emailChangeCooldown').replace(
            '{hours}',
            hours,
          ),
        })
      } else {
        setErrors({
          general:
            errorData?.message || t('changeEmail.errors.changeEmailFailed'),
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    if (otpExpired) {
      setErrors({ otp: t('changeEmail.otpExpiredMessage') })
      return
    }

    if (!isOtpComplete) {
      setErrors({ otp: t('changeEmail.errors.otpRequired') })
      return
    }

    setIsLoading(true)

    try {
      const response = await apiService.post(
        '/api/clients/verifyemailchangeotp',
        {
          clientid: client.clientId,
          tenantid: tenant.tenantid,
          otp: otpValue,
          languagecode: languageCode,
        },
      )

      if (response.success) {
        const pendingNewEmail = response.newEmail || newEmail
        if (pendingNewEmail) {
          sessionStorage.setItem('changeEmailPendingEmail', pendingNewEmail)
        }

        // The backend already expired all auth cookies in the response.
        // A React Router navigate() would lose the race against the session
        // expiry monitor (5 s interval with a stale isAuthenticated closure)
        // which detects the missing ow-c cookie and redirects to /signin.
        // A full page navigation tears down the entire React tree (including
        // the monitor interval) and lets the app re-initialise on the public
        // pending route with no auth state.
        window.location.replace(`/${tenanturlname}/change-email/pending`)
        return
      }

      setErrors({ otp: t('changeEmail.errors.otpInvalid') })
    } catch (error) {
      const errorData = error?.data

      if (
        error?.status === 429 ||
        errorData?.code === 'EMAIL_CHANGE_LIMIT_REACHED'
      ) {
        applyLimitReachedState()
        return
      }

      if (errorData?.code === 'OWMS0009') {
        setIsLocked(true)
        setLockMessage(t('changeEmail.errors.maxAttemptsReached'))
        setErrors({ otp: t('changeEmail.errors.maxAttemptsReached') })
        return
      }

      if (errorData?.code === 'OWER0002') {
        const attemptsLeft = errorData?.attemptsLeft
        if (typeof attemptsLeft === 'number') {
          setErrors({
            otp: t('changeEmail.errors.incorrectCode').replace(
              '{attempts}',
              attemptsLeft,
            ),
          })
        } else {
          setErrors({ otp: t('changeEmail.errors.otpInvalid') })
        }
        return
      }

      setErrors({
        otp: errorData?.message || t('changeEmail.errors.otpInvalid'),
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOtp = async () => {
    if (isLocked) return

    setIsLoading(true)
    setErrors({})

    try {
      const response = await apiService.post(
        '/api/clients/resendemailchangeotp',
        {
          clientid: client.clientId,
          tenantid: tenant.tenantid,
          languagecode: languageCode,
        },
      )

      if (response.success) {
        resetOtpDigits()
        setOtpExpired(false)
        sessionStorage.setItem(
          'otpValidityMinutes',
          String(response.otpValidityMinutes || 60),
        )
        sessionStorage.setItem('otpSentAt', new Date().toISOString())
      }
    } catch (error) {
      if (
        error?.status === 429 ||
        error?.data?.code === 'EMAIL_CHANGE_LIMIT_REACHED'
      ) {
        applyLimitReachedState()
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full px-6 mt-4 md:max-w-[600px] md:mx-auto">
      {step === 'email' && (
        <form
          className="form-container"
          onSubmit={handleEmailSubmit}
          aria-label="Change Email form"
        >
          <div className="text-center mb-6">
            <h1 className="text-xl font-extrabold text-theme-secondary">
              {t('changeEmail.title')}
            </h1>
          </div>

          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid gap-1">
              <label
                htmlFor="new-email"
                className="text-sm font-normal text-theme-accent text-left"
              >
                {t('changeEmail.newEmailLabel')}
              </label>
              <Input
                id="new-email"
                name="new-email"
                placeholder={t('changeEmail.newEmailPlaceholder')}
                autoComplete="email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={isLoading || isLocked}
              />
              {errors.newEmail && (
                <p className="text-sm text-red-600 mt-1">{errors.newEmail}</p>
              )}
            </div>

            <div className="grid gap-1">
              <label
                htmlFor="verify-email"
                className="text-sm font-normal text-theme-accent text-left"
              >
                {t('changeEmail.verifyEmailLabel')}
              </label>
              <Input
                id="verify-email"
                name="verify-email"
                placeholder={t('changeEmail.verifyEmailPlaceholder')}
                autoComplete="email"
                type="email"
                value={verifyEmail}
                onChange={(e) => setVerifyEmail(e.target.value)}
                disabled={isLoading || isLocked}
              />
              {errors.verifyEmail && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.verifyEmail}
                </p>
              )}
            </div>

            <div className="space-y-4 mt-6">
              <Button
                id="change-email-btn"
                variant="primary"
                type="submit"
                size="lg"
                disabled={isLoading || isLocked}
              >
                {isLoading
                  ? t('changeEmail.changingButton')
                  : t('changeEmail.changeButton')}
              </Button>
            </div>
          </div>
        </form>
      )}

      {step === 'otp' && (
        <form
          className="form-container"
          onSubmit={handleOtpSubmit}
          aria-label="OTP Verification form"
        >
          <div className="text-center mb-6">
            <h1 className="text-xl font-extrabold text-theme-secondary">
              {t('changeEmail.otpTitle')}
            </h1>
          </div>

          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}

          {isLocked ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{lockMessage}</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              {/* OTP Code label */}
              <div className="w-full mb-2">
                <label className="text-sm font-normal text-theme-accent text-left block">
                  {t('changeEmail.otpLabel')}
                </label>
              </div>

              {/* 6-digit OTP boxes */}
              <div className="flex gap-3 mb-4">
                {otpDigits.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => {
                      inputRefs.current[idx] = el
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={idx === 0 ? 'one-time-code' : 'off'}
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleDigitChange(idx, e.target.value)}
                    onKeyDown={(e) => handleDigitKeyDown(idx, e)}
                    onPaste={idx === 0 ? handleDigitPaste : undefined}
                    onFocus={(e) => e.target.select()}
                    disabled={isLoading || otpExpired}
                    aria-label={`Digit ${idx + 1}`}
                    className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg
                      focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-theme-primary
                      disabled:bg-gray-100 disabled:text-gray-400
                      text-theme-secondary"
                  />
                ))}
              </div>

              {/* OTP error */}
              {errors.otp && (
                <p className="text-sm text-red-600 mb-3">{errors.otp}</p>
              )}

              {/* OTP sent notification */}
              <p className="text-xs text-center text-theme-secondary mb-1">
                {t('changeEmail.otpSentMessage')}{' '}
                <span className="font-semibold text-theme-primary">
                  {oldEmailForOtp}
                </span>
                . {t('changeEmail.otpSentMessageSuffix')}
              </p>

              {/* Timer / Expired */}
              {otpExpired ? (
                <p className="text-xs text-center text-red-600 mt-2 mb-4">
                  {t('changeEmail.otpExpiredMessage')}
                </p>
              ) : (
                <p className="text-sm text-center text-theme-primary font-medium mt-2 mb-6">
                  {t('changeEmail.otpExpiresIn')}: {otpTimeRemaining || '00:00'}
                </p>
              )}

              {/* Verify / Resend button */}
              <div className="w-full max-w-xs">
                {otpExpired ? (
                  <Button
                    id="resend-otp-btn"
                    variant="primary"
                    type="button"
                    size="lg"
                    onClick={handleResendOtp}
                    disabled={isLoading}
                  >
                    {t('changeEmail.resend')}
                  </Button>
                ) : (
                  <Button
                    id="verify-otp-btn"
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={isLoading || !isOtpComplete}
                  >
                    {isLoading
                      ? t('changeEmail.verifyingButton')
                      : t('changeEmail.verifyButton')}
                  </Button>
                )}
              </div>

              {/* Resend link */}
              {!otpExpired && (
                <div className="text-center mt-4">
                  <p className="text-xs text-theme-secondary">
                    {t('changeEmail.didNotReceiveOtp')}{' '}
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={isLoading || isLocked}
                      className="text-theme-primary font-semibold underline hover:text-theme-primary/80"
                    >
                      {t('changeEmail.resend')}
                    </button>
                  </p>
                </div>
              )}
            </div>
          )}
        </form>
      )}
    </div>
  )
}

export default ChangeEmail
